import { inject, Injectable } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { QUERY_KEYS } from '@core/constans';
import { environment } from '@environments/environment';
import { LegalComplianceService } from '@core/services/legal-compliance.service';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { ApiResponse } from '@core/types';
import {
  LegalComplianceItem,
  LegalComplianceItemCreate,
  LegalComplianceItemUpdate,
} from '@core/types/legal-compliance.type';

@Injectable({ providedIn: 'root' })
export class LegalComplianceQuery {
  private readonly service = inject(LegalComplianceService);
  private readonly queryClient = inject(QueryClient);

  useLegalComplianceGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.legalCompliance.grid(c),
        queryFn: () => lastValueFrom(this.service.grid(c)),
        select: (res: ApiResponse<PaginationResult<LegalComplianceItem>>) => res.data,
        staleTime: environment.staleTime,
      };
    });
  }

  useCreateLegalComplianceItem() {
    return injectMutation(() => ({
      mutationFn: (data: LegalComplianceItemCreate) => lastValueFrom(this.service.create(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.legalCompliance.all }),
    }));
  }

  useUpdateLegalComplianceItem() {
    return injectMutation(() => ({
      mutationFn: (data: LegalComplianceItemUpdate) => lastValueFrom(this.service.update(data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.legalCompliance.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.legalCompliance.all });
      },
    }));
  }

  useDeleteLegalComplianceItem() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.delete(id)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.legalCompliance.all }),
    }));
  }
}

